import { CompanyType, ExtendedCompany, NestedKeys, VisitMode } from "@/types";
import {
  Button,
  Chip,
  DropdownMenu,
  Icon,
  InputField,
  LoadingSpinner,
  ProgressIndicator,
} from "@app-components";
import { createColumnHelper } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import styles from "@/styles/Companies.module.scss";
import Table from "@/components/table/Table.tsx";
import AddCompanyModal from "@/components/building_admin/building_companies/AddCompanyModal";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import {
  companyMunuOverviewOptions,
  extendedCompaniesOptions,
  foodCompanyOptions,
  getExportedBuildingUsers,
  getExportedCompanyUsers,
  useBuilding,
} from "@/api";
import { Tooltip } from "react-tooltip";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import {
  DATE_DAY_MONTH_LOCALE,
  firebaseTimestampToDate,
  timestampSortingFn,
  percentageSortingFn,
  createFoodActiveSortingFn,
  createFoodPercentageSortingFn,
  getPercentage,
} from "@/lib";
import { useIsBuildingAdmin } from "@/hooks/useBuildingAdminAccess";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import CompaniesTableActions from "@/components/building_admin/building_companies/CompaniesTableActions";
import { useBuildingAdminOrReceptionistAccess } from "@/hooks/useReceptionistAccess";
import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import { useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute("/_portal/building/companies/")({
  component: Companies,
  loader: () => {
    const { queryClient } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.ensureQueryData(extendedCompaniesOptions);
  },
});

type SearchableColumns = NestedKeys<ExtendedCompany>;
const searchableColumns: Array<SearchableColumns> = ["buildingTenantName"];

const columnHelper = createColumnHelper<ExtendedCompany>();

function Companies() {
  const { t, i18n } = useTranslation();
  const { allowBuildingAdminOrReceptionistAccess } =
    useBuildingAdminOrReceptionistAccess();
  const { isBuildingAdmin } = useIsBuildingAdmin();
  const { hasEitherFoodSystem, hasMunu } = useWelcomeFoodAccess(true);
  const { data: building } = useBuilding();
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isLoading, data: companies } = useBuildingQuery(
    extendedCompaniesOptions,
  );
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [loadingAllUsers, setLoadingAllUsers] = useState(false);

  const totalUsers = useMemo(
    () =>
      companies?.reduce(
        (acc: number, company: ExtendedCompany) => acc + company.totalUsers,
        0,
      ) || 0,
    [companies],
  );
  const totalActiveUsers = useMemo(
    () =>
      companies?.reduce(
        (acc: number, company: ExtendedCompany) => acc + company.activeUsers,
        0,
      ) || 0,
    [companies],
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState<ExtendedCompany[]>(
    [],
  );

  useEffect(() => {
    if (searchTerm && !!companies?.length) {
      const searchTerms = searchTerm.toLowerCase().split(" ");
      setFilteredCompanies(
        companies.filter((company: ExtendedCompany) =>
          searchTerms.every((term) =>
            searchableColumns.some((column) =>
              (
                company[column as keyof ExtendedCompany]
                  ?.toString()
                  .toLowerCase() || ""
              ).includes(term.toLowerCase()),
            ),
          ),
        ),
      );
    } else {
      setFilteredCompanies(companies ?? []);
    }
  }, [searchTerm, companies]);

  const exportCompanyUsers = async (
    companyId: string,
    companyName: string,
    index: number,
    excel?: boolean,
  ) => {
    setLoadingIndex(index);
    try {
      await getExportedCompanyUsers(companyId, companyName, { excel });
    } finally {
      setLoadingIndex(null);
    }
  };

  const exportAllBuildingUsers = async (excel?: boolean) => {
    setLoadingAllUsers(true);
    try {
      await getExportedBuildingUsers(building.displayName, { excel });
    } finally {
      setLoadingAllUsers(false);
    }
  };

  const columns = useMemo(
    // memoize so columns don't need to be "re-rendered" when component changes
    () => [
      columnHelper.accessor("buildingTenantName", {
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANY_SETTINGS.COMPANY_NAME")}
          />
        ),
        meta: {
          label: t("COMPANY_SETTINGS.COMPANY_NAME"),
        },
      }),
      columnHelper.accessor("companyType", {
        cell: (info) => {
          const type = info.getValue() || CompanyType.TENANT;
          return (
            <div>{t(`ADD_COMPANY.COMPANY_TYPE.${type.toUpperCase()}`)}</div>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("ADD_COMPANY.COMPANY_TYPE.LABEL")}
          />
        ),
        sortingFn: (a, b) => {
          const aValue = a.original.companyType || CompanyType.TENANT;
          const bValue = b.original.companyType || CompanyType.TENANT;
          return aValue.localeCompare(bValue);
        },
        meta: {
          label: t("ADD_COMPANY.COMPANY_TYPE.LABEL"),
        },
      }),
      columnHelper.accessor("activeUsers", {
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANIES_TABLE.ACTIVATED_USERS")}
          />
        ),
        meta: {
          label: t("COMPANIES_TABLE.ACTIVATED_USERS"),
        },
      }),
      columnHelper.accessor("totalUsers", {
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANIES_TABLE.TOTAL_USERS")}
          />
        ),
        meta: {
          label: t("COMPANIES_TABLE.TOTAL_USERS"),
        },
      }),
      columnHelper.accessor((row) => [row.activeUsers, row.totalUsers], {
        id: "users-count",
        cell: (info) => {
          const [activeUsers, maxUsers] = info.getValue();
          const percentage = getPercentage(activeUsers, maxUsers);
          return (
            <>
              {activeUsers} / {maxUsers}
              <span
                style={{
                  paddingLeft: "var(--wlcm-spacing-xs)",
                  color:
                    percentage < 10
                      ? "var(--wlcm-color-danger-red)"
                      : percentage < 40
                        ? "var(--wlcm-color-warning-orange)"
                        : "var(--wlcm-color-success-green)",
                }}
              >
                {" "}
                ({percentage}%)
              </span>
            </>
          );
        },
        header: (info) => (
          <SortableTableHeader info={info} label={t("COMPANIES_TABLE.USERS")} />
        ),
        sortingFn: percentageSortingFn(),
        meta: {
          label: t("COMPANIES_TABLE.USERS"),
        },
      }),
      ...(building?.accessControlSystemIntegration
        ? [
            columnHelper.accessor("accessControlSystemIntegration", {
              cell: (info) => {
                const integration = info.getValue();
                return (
                  <div className={styles.tableCell}>
                    <div
                      aria-hidden
                      className={styles.statusIndicator}
                      style={{
                        background: integration
                          ? "var(--wlcm-color-success-green)"
                          : "var(--wlcm-color-danger-red)",
                      }}
                    ></div>
                    <span>
                      {integration
                        ? t("COMPANY_SETTINGS.ACCESS_CONTROL_SYSTEM.INTEGRATED")
                        : t(
                            "COMPANY_SETTINGS.ACCESS_CONTROL_SYSTEM.NOT_INTEGRATED",
                          )}
                    </span>
                  </div>
                );
              },
              header: (info) => (
                <SortableTableHeader
                  info={info}
                  label={t("COMPANY_SETTINGS.ACCESS_CONTROL_SYSTEM.LABEL")}
                />
              ),
              sortingFn: (a, b) => {
                // Make sure companies with no value are treated as false
                const aValue = !!a.original.accessControlSystemIntegration;
                const bValue = !!b.original.accessControlSystemIntegration;
                if (aValue === bValue) return 0;
                return aValue ? -1 : 1;
              },
              meta: {
                label: t("COMPANY_SETTINGS.ACCESS_CONTROL_SYSTEM.LABEL"),
              },
            }),
          ]
        : []),
      ...(building?.enableWelcomeVisit
        ? [
            columnHelper.accessor("visitMode", {
              cell: (info) => {
                const mode = info.getValue().toUpperCase();
                return (
                  <div className={styles.tableCell}>
                    <div
                      aria-hidden
                      className={styles.statusIndicator}
                      style={{
                        background:
                          mode === "ENABLED"
                            ? "var(--wlcm-color-success-green)"
                            : mode === "DISABLED"
                              ? "var(--wlcm-color-danger-red)"
                              : "var(--wlcm-color-warning-yellow)",
                      }}
                    ></div>
                    <span>
                      {t(`COMPANY_SETTINGS.VISIT_MODE.${mode}.LABEL`)}
                    </span>
                  </div>
                );
              },
              header: (info) => (
                <SortableTableHeader
                  info={info}
                  label={t("COMPANY_SETTINGS.VISIT_MODE.LABEL")}
                />
              ),
              sortingFn: (a, b) => {
                const order = {
                  [VisitMode.ENABLED]: 0,
                  [VisitMode.LIMITED]: 1,
                  [VisitMode.DISABLED]: 2,
                };
                const aValue = order[a.original.visitMode] ?? 2;
                const bValue = order[b.original.visitMode] ?? 2;
                return aValue - bValue;
              },
              meta: {
                label: t("COMPANY_SETTINGS.VISIT_MODE.LABEL"),
              },
            }),
          ]
        : []),
      ...(hasEitherFoodSystem
        ? [
            columnHelper.accessor("buildingTenantId", {
              id: "food-active",
              cell: (info) => {
                const companyId = info.getValue();
                return <FoodCell companyId={companyId} hasMunu={hasMunu} />;
              },
              header: (info) => (
                <SortableTableHeader
                  info={info}
                  label={t("COMPANIES_TABLE.FOOD_ACTIVE")}
                />
              ),
              sortingFn: createFoodActiveSortingFn(queryClient, hasMunu),
              sortUndefined: -1,
              meta: {
                label: t("COMPANIES_TABLE.FOOD_ACTIVE"),
              },
            }),
            columnHelper.accessor("foodMaxIntegratedUsers", {
              id: "food-limit",
              cell: (info) => {
                const maxUsers = info.getValue();
                return maxUsers !== undefined ? maxUsers : "-";
              },
              header: (info) => (
                <SortableTableHeader
                  info={info}
                  label={t("COMPANIES_TABLE.FOOD_LIMIT")}
                />
              ),
              meta: {
                label: t("COMPANIES_TABLE.FOOD_LIMIT"),
              },
            }),
            columnHelper.accessor("foodMaxIntegratedUsers", {
              id: "food-users-count",
              cell: (info) => {
                const companyId = info.row.original.buildingTenantId;

                const maxUsers = info.getValue();
                return (
                  <FoodUserCountCell
                    maxUsers={maxUsers}
                    companyId={companyId}
                    hasMunu={hasMunu}
                  />
                );
              },
              header: (info) => (
                <SortableTableHeader
                  info={info}
                  label={t("COMPANIES_TABLE.FOOD_USERS")}
                />
              ),
              sortingFn: createFoodPercentageSortingFn(queryClient, hasMunu),
              meta: {
                label: t("COMPANIES_TABLE.FOOD_USERS"),
              },
            }),
          ]
        : []),
      columnHelper.accessor("createdAt", {
        cell: (info) => {
          const value = info.getValue();

          return (
            <div className={`${styles.iconContainer} ${styles.noPadding}`}>
              {value ? (
                <>{`${DATE_DAY_MONTH_LOCALE(i18n.language).format(firebaseTimestampToDate(value))}`}</>
              ) : (
                <>-</>
              )}
            </div>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("USERS.TABLE_HEADERS.CREATED_AT")}
          />
        ),
        meta: {
          label: t("USERS.TABLE_HEADERS.CREATED_AT"),
        },
        sortingFn: timestampSortingFn,
        sortUndefined: -1,
      }),
      columnHelper.accessor("ssoSettings", {
        cell: (info) => {
          const ssoSettings = info.getValue();
          if (!ssoSettings?.length) return "-";
          const firstDomain = ssoSettings[0].domain;
          const domainOverflow = ssoSettings
            .slice(1)
            .map((s) => s.domain)
            .filter(Boolean);
          const domainAsId = firstDomain.replace(".", "-");
          return (
            <div className={styles.tableCell}>
              <Chip className="tableTag" label={firstDomain} />
              {!!domainOverflow.length && (
                <>
                  <span
                    id={`${domainAsId}-sso-overflow`}
                    style={{ fontWeight: "bold", opacity: 0.5 }}
                  >{`+${domainOverflow.length}`}</span>
                  <Tooltip
                    anchorSelect={`#${domainAsId}-sso-overflow`}
                    content={domainOverflow.join(", ")}
                    style={{ maxWidth: 300 }}
                  />
                </>
              )}
            </div>
          );
        },
        header: (info) => <SortableTableHeader info={info} label="SSO" />,
        sortingFn: (a, b) => {
          const aValue = a.original.ssoSettings?.length || 0;
          const bValue = b.original.ssoSettings?.length || 0;
          return aValue - bValue;
        },
        meta: {
          label: "SSO",
        },
      }),
      ...(isBuildingAdmin
        ? [
            columnHelper.display({
              id: "actions",
              cell: ({ row }) => {
                const isLoading = loadingIndex === row.index;
                return (
                  <CompaniesTableActions
                    row={row}
                    isLoading={isLoading}
                    exportCompanyUsers={exportCompanyUsers}
                  />
                );
              },
              header: () => (
                <div className="sr-only">
                  {t("COMPANIES_TABLE.EXPORT_USERS")}
                </div>
              ),
            }),
          ]
        : []),
    ],
    [
      loadingIndex,
      building.enableWelcomeVisit,
      building.accessControlSystemIntegration,
      hasEitherFoodSystem,
      hasMunu,
      isBuildingAdmin,
    ],
  );

  if (!allowBuildingAdminOrReceptionistAccess) {
    return null;
  }

  return (
    <>
      <div className="pageHeader">
        <h1>{t("PAGES.COMPANIES.HEADER")}</h1>
        <p>{t("PAGES.COMPANIES.SUBHEADER")}</p>
      </div>
      <div className={styles.topRow}>
        <InputField
          label={t("PAGES.COMPANIES.SEARCH")}
          placeholder={t("PAGES.COMPANIES.SEARCH") + "..."}
          icon="search"
          value={searchTerm}
          compact
          hideLabel
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {isBuildingAdmin && (
          <Button icon="add" onClick={() => setAddCompanyOpen(true)}>
            {t("PAGES.COMPANIES.ADD")}
          </Button>
        )}
      </div>
      <Table
        name="companies"
        data={filteredCompanies}
        columns={columns}
        isLoading={isLoading}
        onRowClick={({ buildingTenantId }) => {
          navigate({
            to: "/building/companies/$companyId",
            params: {
              companyId: buildingTenantId,
            },
          });
        }}
        onRowHover={({ buildingTenantId }) => {
          router.preloadRoute({
            to: "/building/companies/$companyId",
            params: {
              companyId: buildingTenantId,
            },
          });
        }}
        defaultColumnVisibility={{
          activeUsers: false,
          totalUsers: false,
          "food-active": false,
          "food-limit": false,
          "food-users-count": false,
          createdAt: false,
        }}
        defaultSorting={[
          {
            id: "users-count",
            desc: true,
          },
        ]}
        enableColumnControls
      />
      <div className={styles.bottomContainer}>
        <ProgressIndicator
          id="companies-active-users"
          label={(data) => (
            <span
              style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
            >
              <Icon name="groups" />
              <span>
                <Trans
                  i18nKey="COMPANIES_TABLE.TOTAL_ACTIVE_USERS"
                  values={data}
                  components={{
                    strong: <strong />,
                    green: (
                      <span
                        style={{ color: "var(--wlcm-color-success-green)" }}
                      />
                    ),
                    grey: <span style={{ opacity: 0.6 }} />,
                  }}
                />
              </span>
            </span>
          )}
          total={totalUsers}
          value={totalActiveUsers}
          barColor="var(--wlcm-color-success-green)"
        />
        {isBuildingAdmin && (
          <DropdownMenu
            id="building-users-export"
            activator={
              <Button
                isLoading={loadingAllUsers}
                disabled={loadingAllUsers}
                dark
                secondary
                icon="file_export"
              >
                {t("COMPANIES_TABLE.EXPORT_ALL_USERS")}
              </Button>
            }
            menuOptions={[
              { icon: "csv", label: "CSV", onSelect: exportAllBuildingUsers },
              {
                icon: "microsoft_excel",
                label: "Microsoft Excel",
                onSelect: () => exportAllBuildingUsers(true),
              },
            ]}
          />
        )}
      </div>
      <AddCompanyModal
        onClose={() => setAddCompanyOpen(false)}
        open={addCompanyOpen}
      />
    </>
  );
}

const FoodCell = ({
  companyId,
  hasMunu,
}: {
  companyId: string;
  hasMunu: boolean;
}) => {
  const { data: foodCompany, isLoading: foodCompanyLoading } = useBuildingQuery(
    hasMunu
      ? companyMunuOverviewOptions(companyId)
      : foodCompanyOptions(companyId),
  );
  if (foodCompanyLoading) return <LoadingSpinner size={12} />;
  const { activeUsers, integratedUsersCount } = foodCompany || {};
  return activeUsers !== undefined
    ? activeUsers
    : integratedUsersCount !== undefined
      ? integratedUsersCount
      : "-";
};

const FoodUserCountCell = ({
  companyId,
  hasMunu,
  maxUsers,
}: {
  companyId: string;
  hasMunu: boolean;
  maxUsers?: number;
}) => {
  const { data: foodCompany, isLoading: foodCompanyLoading } = useBuildingQuery(
    hasMunu
      ? companyMunuOverviewOptions(companyId)
      : foodCompanyOptions(companyId),
  );
  if (foodCompanyLoading) return <LoadingSpinner size={12} />;
  const { activeUsers, integratedUsersCount } = foodCompany || {};
  const val = activeUsers || integratedUsersCount;
  const bothUndefined = val === undefined && maxUsers === undefined;
  if (bothUndefined) return "-";
  const percentage = getPercentage(val, maxUsers);
  return (
    <>
      {val ?? 0} / {maxUsers || "-"}
      {maxUsers !== undefined && (
        <span
          style={{
            paddingLeft: "var(--wlcm-spacing-xs)",
            color:
              percentage <= 90
                ? "var(--wlcm-color-success-green)"
                : percentage > 97
                  ? "var(--wlcm-color-danger-red)"
                  : "var(--wlcm-color-warning-orange)",
          }}
        >
          {" "}
          ({percentage}%)
        </span>
      )}
    </>
  );
};
